import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import ShareModal from "../components/ShareModal";

export default function Question() {
  const { qid } = useParams();
  const navigate = useNavigate();
  const { t, pick } = useI18n();

  const [questions, setQuestions] = useState([]);
  const [myVotes, setMyVotes] = useState({});
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);

  const loadAll = async () => {
    try {
      const [qRes, mvRes, rRes] = await Promise.all([
        api.get("/questions"), api.get("/vote/mine"), api.get("/results"),
      ]);
      setQuestions(qRes.data.questions);
      setMyVotes(mvRes.data.votes || {});
      setResults(rRes.data.results);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/results");
        setResults(data.results);
      } catch {}
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const question = useMemo(() => questions.find((q) => q.id === qid), [questions, qid]);
  const result = useMemo(() => results.find((r) => r.id === qid), [results, qid]);
  const currentIndex = useMemo(() => questions.findIndex((q) => q.id === qid), [questions, qid]);

  const prevQ = currentIndex > 0 ? questions[currentIndex - 1] : null;
  const nextQ = currentIndex >= 0 && currentIndex < questions.length - 1 ? questions[currentIndex + 1] : null;

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4]" data-testid="question-loading">
        <div className="font-mono-civic text-sm uppercase tracking-widest text-black">{t("q_loading")}</div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center bg-[#F4F4F4] text-black gap-4 px-5">
        <div className="font-serif-display text-4xl" style={{ fontWeight: 900 }}>{t("q_not_found")}</div>
        <Link to="/vote" className="bg-black text-white uppercase tracking-widest px-6 py-3 border-2 border-black" style={{ fontWeight: 900 }}>
          {t("q_back_ballot")}
        </Link>
      </div>
    );
  }

  const choice = myVotes[question.id];
  const yes = result?.yes ?? 0;
  const no = result?.no ?? 0;
  const total = yes + no;
  const yesPct = result?.yes_pct ?? 0;
  const noPct = result?.no_pct ?? 0;
  const text = pick(question, "text");
  const funFact = pick(question, "fun_fact");

  const castVote = async (answer) => {
    if (saving) return;
    setError("");
    setSaving(true);
    const prev = myVotes[question.id];
    setMyVotes((m) => ({ ...m, [question.id]: answer }));
    setResults((rs) => rs.map((r) => {
      if (r.id !== question.id) return r;
      let yc = r.yes, nc = r.no;
      if (prev === "YES") yc = Math.max(0, yc - 1);
      if (prev === "NO") nc = Math.max(0, nc - 1);
      if (answer === "YES") yc += 1;
      if (answer === "NO") nc += 1;
      const t2 = yc + nc;
      return { ...r, yes: yc, no: nc, total: t2,
        yes_pct: t2 ? Math.round((yc / t2) * 1000) / 10 : 0,
        no_pct: t2 ? Math.round((nc / t2) * 1000) / 10 : 0 };
    }));
    try {
      await api.post("/vote", { question_id: question.id, answer });
      const { data } = await api.get("/results");
      setResults(data.results);
    } catch (err) {
      setError(formatApiError(err));
      setMyVotes((m) => ({ ...m, [question.id]: prev }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black px-5 py-10" data-testid="question-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link to="/vote" className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black underline" data-testid="back-to-ballot">
            {t("q_back_ballot")}
          </Link>
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black" data-testid="question-counter">
            {t("q_counter_q")} {String(currentIndex + 1).padStart(2, "0")} / {String(questions.length).padStart(2, "0")}
          </div>
        </div>

        <div className={`mt-6 bg-white border-2 border-black p-6 sm:p-10 ${question.main ? "brutal-shadow-red" : "brutal-shadow"}`} data-testid="question-statement">
          {question.main && (
            <div className="font-mono-civic text-[10px] uppercase tracking-widest bg-[#E00000] text-white inline-block px-2 py-1 mb-4">
              {t("q_headline_tag")}
            </div>
          )}
          <h1 className={`font-serif-display text-black leading-[1.02] ${question.main ? "italic text-4xl sm:text-5xl md:text-6xl" : "text-3xl sm:text-4xl md:text-5xl"}`} style={{ fontWeight: 800 }}>
            {text}
          </h1>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 sm:gap-6">
          <button onClick={() => castVote("YES")} disabled={saving}
            className={`py-8 sm:py-10 border-2 border-black uppercase tracking-widest transition-all disabled:opacity-60 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none ${choice === "YES" ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"}`}
            data-testid="qpage-vote-yes" style={{ fontWeight: 900 }}>
            <span className="font-serif-display text-4xl sm:text-5xl block" style={{ fontWeight: 800 }}>{t("q_yes")}</span>
            <span className="font-mono-civic text-[10px] uppercase tracking-widest block mt-1 opacity-70">{t("q_tap_yes")}</span>
          </button>
          <button onClick={() => castVote("NO")} disabled={saving}
            className={`py-8 sm:py-10 border-2 border-black uppercase tracking-widest transition-all disabled:opacity-60 brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none ${choice === "NO" ? "bg-[#E00000] text-white border-[#E00000]" : "bg-white text-black hover:bg-[#E00000] hover:text-white hover:border-[#E00000]"}`}
            data-testid="qpage-vote-no" style={{ fontWeight: 900 }}>
            <span className="font-serif-display text-4xl sm:text-5xl block" style={{ fontWeight: 800 }}>{t("q_no")}</span>
            <span className="font-mono-civic text-[10px] uppercase tracking-widest block mt-1 opacity-70">{t("q_tap_no")}</span>
          </button>
        </div>

        {choice && (
          <div className="mt-4 font-mono-civic text-xs uppercase tracking-widest text-black text-center" data-testid="qpage-current-vote">
            {t("q_current_vote")} <span className="text-[#E00000]" style={{ fontWeight: 900 }}>{choice === "YES" ? t("q_yes") : t("q_no")}</span>
          </div>
        )}

        {error && (
          <div className="mt-4 border-2 border-[#E00000] bg-white p-3 text-[#E00000] text-sm" style={{ fontWeight: 800 }} data-testid="qpage-error">{error}</div>
        )}

        <div className="mt-12 flex items-center justify-between">
          <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black flex items-center gap-2">
            <span className="w-2 h-2 bg-[#E00000] rounded-full animate-pulse" />
            {t("q_live_tally")}
          </div>
          <div className="font-mono-civic text-xs uppercase tracking-widest text-black">
            {t("q_total")} <span className="text-black" style={{ fontWeight: 900 }} data-testid="qpage-total-votes">{total.toLocaleString()}</span>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white border-2 border-black brutal-shadow-sm p-6 sm:p-8 text-center" data-testid="qpage-yes-box">
            <div className="font-mono-civic text-xs sm:text-sm uppercase tracking-[0.3em] text-black">{t("q_left_yes")}</div>
            <div className="mt-2 font-serif-display text-black leading-none text-7xl sm:text-8xl md:text-9xl" style={{ fontWeight: 800 }} data-testid="qpage-yes-count">
              {yes.toLocaleString()}
            </div>
            <div className="mt-3 inline-block bg-black text-white font-mono-civic text-xs uppercase tracking-widest px-3 py-1" style={{ fontWeight: 800 }}>
              {t("q_yes")} · {yesPct}%
            </div>
          </div>
          <div className="bg-white border-2 border-black brutal-shadow-sm p-6 sm:p-8 text-center" data-testid="qpage-no-box">
            <div className="font-mono-civic text-xs sm:text-sm uppercase tracking-[0.3em] text-black">{t("q_right_no")}</div>
            <div className="mt-2 font-serif-display text-[#E00000] leading-none text-7xl sm:text-8xl md:text-9xl" style={{ fontWeight: 800 }} data-testid="qpage-no-count">
              {no.toLocaleString()}
            </div>
            <div className="mt-3 inline-block bg-[#E00000] text-white font-mono-civic text-xs uppercase tracking-widest px-3 py-1" style={{ fontWeight: 800 }}>
              {t("q_no")} · {noPct}%
            </div>
          </div>
        </div>

        {total > 0 && (
          <div className="mt-6 border-2 border-black bg-white p-3" data-testid="qpage-proportion-bar">
            <div className="flex h-6 border-2 border-black overflow-hidden">
              <div className="bg-black" style={{ width: `${yesPct}%` }} />
              <div className="bg-[#E00000]" style={{ width: `${noPct}%` }} />
            </div>
          </div>
        )}

        {funFact && (
          <div className="mt-10 bg-white border-2 border-black brutal-shadow-sm p-6 sm:p-7 relative" data-testid="qpage-fun-fact">
            <div className="absolute -top-3 left-4 bg-[#E00000] text-white font-mono-civic text-[10px] uppercase tracking-[0.3em] px-3 py-1" style={{ fontWeight: 900 }}>
              {t("q_fun_fact")}
            </div>
            <div className="flex gap-4">
              <div className="font-serif-display text-5xl text-[#E00000] leading-none shrink-0" style={{ fontWeight: 800 }} aria-hidden>★</div>
              <p className="text-black text-base sm:text-lg leading-relaxed" style={{ fontWeight: 700 }}>
                {funFact}
              </p>
            </div>
            <div className="mt-4 font-mono-civic text-[10px] uppercase tracking-widest text-black/60" style={{ fontWeight: 700 }}>
              {t("q_neutral_context")}
            </div>
          </div>
        )}

        <div className="mt-8">
          <button onClick={() => setShareOpen(true)} disabled={!result || result.total === 0}
            className="w-full bg-white text-black uppercase tracking-widest py-4 sm:py-5 border-2 border-black hover:bg-[#E00000] hover:text-white hover:border-[#E00000] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ fontWeight: 900 }} data-testid="qpage-share-btn">
            {result && result.total > 0 ? t("q_share") : t("q_share_no")}
          </button>
        </div>

        <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} question={question} result={result} />

        <div className="mt-12 border-t-2 border-black pt-6 grid grid-cols-2 gap-3 sm:gap-4">
          {prevQ ? (
            <button onClick={() => navigate(`/vote/${prevQ.id}`)}
              className="bg-white text-black uppercase tracking-widest py-4 sm:py-5 border-2 border-black hover:bg-black hover:text-white transition-colors text-left px-4"
              style={{ fontWeight: 800 }} data-testid="qpage-prev-btn">
              <div className="font-mono-civic text-[10px] opacity-70">{t("q_previous")}</div>
              <div className="truncate">{currentIndex.toString().padStart(2, "0")} · {pick(prevQ, "text").split(" ").slice(0, 4).join(" ")}…</div>
            </button>
          ) : (
            <Link to="/vote"
              className="bg-white text-black uppercase tracking-widest py-4 sm:py-5 border-2 border-black hover:bg-black hover:text-white transition-colors px-4 flex items-center"
              style={{ fontWeight: 800 }} data-testid="qpage-back-btn">
              {t("q_all_questions")}
            </Link>
          )}
          {nextQ ? (
            <button onClick={() => navigate(`/vote/${nextQ.id}`)}
              className="bg-black text-white uppercase tracking-widest py-4 sm:py-5 border-2 border-black brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all text-right px-4"
              style={{ fontWeight: 800 }} data-testid="qpage-next-btn">
              <div className="font-mono-civic text-[10px] opacity-70">{t("q_next")}</div>
              <div className="truncate">{(currentIndex + 2).toString().padStart(2, "0")} · {pick(nextQ, "text").split(" ").slice(0, 4).join(" ")}…</div>
            </button>
          ) : (
            <Link to="/results"
              className="bg-black text-white uppercase tracking-widest py-4 sm:py-5 border-2 border-black brutal-shadow-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all px-4 flex items-center justify-end"
              style={{ fontWeight: 800 }} data-testid="qpage-finish-btn">
              {t("q_finish")}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
